import React from 'react';
import { Section } from '../layout/Section';
import { Map } from '../features/Map';
import { generateDirectionsUrl } from '../../services/googleMaps';
import { RESTAURANT_INFO } from '../../utils/constants';

/**
 * LocationSection Component
 * 
 * Displays the restaurant's location with an interactive map,
 * full address, and a "Route planen" button.
 * 
 * Requirements:
 * - 7.1: Display interactive map at restaurant location
 * - 7.2: Provide "Route planen" button
 * - 7.3: Display full address
 * - 7.4: Handle missing API key gracefully
 * - 7.5: Responsive map sizing
 */
export const LocationSection: React.FC = () => {
  const address = RESTAURANT_INFO.address;
  const directionsUrl = generateDirectionsUrl(address);

  return (
    <Section
      id="standort"
      variant="dark"
      aria-labelledby="location-heading"
    >
      {/* Section Header */}
      <div className="text-center mb-12">
        <h2
          id="location-heading"
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
        >
          Standort
        </h2>
        <div className="w-24 h-1 bg-[#c9a84c] mx-auto rounded-full" aria-hidden="true" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
        {/* Map */}
        <div className="order-2 lg:order-1">
          <Map
            address={address}
            height="380px"
            className="w-full shadow-lg"
          />
        </div>

        {/* Address & Directions */}
        <div className="order-1 lg:order-2 flex flex-col gap-6">
          {/* Address */}
          <div>
            <h3 className="text-xl font-semibold text-[#c9a84c] mb-3">
              Adresse
            </h3>
            <address
              className="not-italic text-gray-300 text-lg leading-relaxed"
              aria-label="Restaurantadresse"
            >
              <span className="block font-semibold text-white">Fey am Tibarg</span>
              <span className="block">Tibarg 16</span>
              <span className="block">22459 Hamburg</span>
            </address>
          </div>

          {/* Directions Button */}
          <div>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#c9a84c] text-[#1a1a1a] font-semibold text-lg hover:bg-[#b8963e] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              aria-label="Route zu Restaurant Fey planen (öffnet Google Maps in neuem Tab)"
            >
              {/* Navigation icon */}
              <svg
                width={20}
                height={20}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polygon points="3 11 22 2 13 21 11 13 3 11" />
              </svg>
              Route planen
            </a>
          </div>

          {/* Additional info */}
          <div className="text-gray-400 text-sm">
            <p>
              Wir befinden uns am Tibarg Süd in Hamburg-Niendorf –
              gut erreichbar mit öffentlichen Verkehrsmitteln und dem eigenen PKW.
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
};
